import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Slider } from "../ui/slider";
import { Button } from "../ui/button";
import { useLead } from "../../context/LeadContext";
import { Clock, TrendingUp, Users, ArrowRight } from "lucide-react";

const fmtMoney = (n) => {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(n >= 1e8 ? 0 : 1)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(n >= 1e6 ? 0 : 1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(n >= 1e4 ? 0 : 1)}k`;
  return `₹${Math.round(n)}`;
};
const fmtFull = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

// share of qualification effort the AI agent removes
const AUTOMATION = 0.85;
// extra leads recovered from faster + always-on response
const RECOVERY = 0.28;

export const RoiCalculator = () => {
  const { openLeadForm } = useLead();
  const [leads, setLeads] = useState(600);
  const [hourly, setHourly] = useState(300);
  const [minutes, setMinutes] = useState(7);

  const r = useMemo(() => {
    const manualHours = (leads * minutes) / 60;
    const hoursSaved = manualHours * AUTOMATION;
    const moneySaved = hoursSaved * hourly;
    const annual = moneySaved * 12;
    const recovered = Math.round(leads * RECOVERY);
    return {
      hoursSaved: Math.round(hoursSaved),
      moneySaved,
      annual,
      recovered,
    };
  }, [leads, hourly, minutes]);

  return (
    <section id="roi" className="mx-auto max-w-6xl px-5 py-28 sm:py-32" data-testid="roi-calculator">
      <div className="mb-12 max-w-2xl">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">ROI Calculator</span>
        <h2 className="mt-4 font-display text-4xl font-black tracking-tighter text-charcoal sm:text-5xl">
          See what manual qualification really costs you.
        </h2>
        <p className="mt-4 text-base text-silver-700 sm:text-lg">
          Drag the sliders to match your business. The numbers update live.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* inputs */}
        <div className="rounded-3xl border border-silver-300/60 bg-white p-7 sm:p-9 lg:col-span-5">
          <SliderRow
            label="Monthly lead volume"
            value={leads.toLocaleString()}
            min={50}
            max={5000}
            step={50}
            v={leads}
            onChange={setLeads}
            testid="roi-leads"
          />
          <SliderRow
            label="Hourly cost of manual qualification"
            value={`₹${hourly}/hr`}
            min={50}
            max={3000}
            step={50}
            v={hourly}
            onChange={setHourly}
            testid="roi-hourly"
          />
          <SliderRow
            label="Minutes spent qualifying each lead"
            value={`${minutes} min`}
            min={2}
            max={20}
            step={1}
            v={minutes}
            onChange={setMinutes}
            testid="roi-minutes"
            last
          />
        </div>

        {/* output */}
        <div className="relative overflow-hidden rounded-3xl bg-charcoal p-7 text-white sm:p-9 lg:col-span-7">
          <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-brand/30 blur-3xl" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Your savings with Digimonk.AI</p>

          <div className="mt-5">
            <motion.p
              key={Math.round(r.moneySaved)}
              initial={{ opacity: 0.4, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-display text-6xl font-black leading-none tracking-tighter sm:text-7xl"
              data-testid="roi-money-saved"
            >
              {fmtFull(r.moneySaved)}
            </motion.p>
            <p className="mt-2 text-sm text-white/60">saved every month on qualification labour</p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Stat icon={Clock} value={`${r.hoursSaved.toLocaleString()} hrs`} label="freed up / month" />
            <Stat icon={TrendingUp} value={fmtMoney(r.annual)} label="saved / year" testid="roi-annual" />
            <Stat icon={Users} value={`+${r.recovered.toLocaleString()}`} label="leads recovered / mo" />
          </div>

          <Button
            data-testid="roi-cta-btn"
            onClick={() =>
              openLeadForm({
                source: "roi_calculator",
                qualification: { leads, hourly, minutes, monthly_saving: Math.round(r.moneySaved) },
              })
            }
            className="group mt-8 h-12 w-full bg-brand text-base font-bold text-white hover:bg-brand-hover sm:w-auto sm:px-8"
          >
            Capture these savings, get my demo
            <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </div>
    </section>
  );
};

const SliderRow = ({ label, value, min, max, step, v, onChange, testid, last }) => (
  <div className={last ? "" : "mb-8"}>
    <div className="mb-3 flex items-end justify-between">
      <span className="text-sm font-semibold text-silver-700">{label}</span>
      <span className="font-display text-xl font-extrabold text-charcoal" data-testid={`${testid}-value`}>
        {value}
      </span>
    </div>
    <Slider
      data-testid={testid}
      value={[v]}
      min={min}
      max={max}
      step={step}
      onValueChange={(val) => onChange(val[0])}
    />
  </div>
);

const Stat = ({ icon: Icon, value, label, testid }) => (
  <div className="rounded-2xl bg-white/5 p-4 ring-1 ring-white/10">
    <Icon className="mb-2 h-5 w-5 text-brand" />
    <p className="font-display text-2xl font-extrabold tracking-tight" data-testid={testid}>
      {value}
    </p>
    <p className="text-xs text-white/55">{label}</p>
  </div>
);

export default RoiCalculator;
